import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../_services/auth.service';
import { FirstPasswordRequestDto, TokenDto } from '../../_services/auth.dto';
import { NotificationService } from '../../_services/notification.service';
import { SettingsService } from '../../_services/settings.service';
import { StorageService } from '../../_services/storage.service';
import { UserService } from '../../_services/user.service';

// Règle de la page compte, portée à 12 caractères pour les comptes administrateurs
const PASSWORD_PATTERN = /^(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*()_+])[0-9a-zA-Z!@#$%^&*()_+]{12,}$/;

export interface FirstLoginState {
  username: string;
  password: string;
}

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const password2 = group.get('password2')?.value;
  return !password2 || password === password2 ? null : { mismatch: true };
}

@Component({
    selector: 'app-first-login',
    templateUrl: './first-login.component.html',
    styleUrls: ['./first-login.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FirstLoginComponent implements OnInit {
  passwordForm!: FormGroup;
  username = '';
  isSubmitting = false;
  private temporaryPassword = '';

  constructor(
    private formBuilder: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private storageService: StorageService,
    private userService: UserService,
    private notificationService: NotificationService,
    private settingsService: SettingsService
  ) { }

  ngOnInit() {
    this.passwordForm = this.formBuilder.group({
      password: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
      password2: ['', Validators.required]
    }, { validators: passwordsMatch });

    if (!this.readCredentials()) {
      this.router.navigateByUrl('/login');
    }
  }

  /** Reprend les identifiants transmis par la page de connexion et les retire de l'historique. */
  private readCredentials(): boolean {
    const { username, password, ...rest } = (history.state ?? {}) as Partial<FirstLoginState>;
    history.replaceState(rest, '');

    if (!username || !password) {
      return false;
    }

    this.username = username;
    this.temporaryPassword = password;
    return true;
  }

  get mismatch(): boolean {
    return this.passwordForm.hasError('mismatch') && !!this.passwordForm.get('password2')?.dirty;
  }

  get weakPassword(): boolean {
    const control = this.passwordForm.get('password');
    return !!control?.dirty && control.hasError('pattern');
  }

  submit(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      this.notificationService.addNotification({
        type: 'alert',
        message: 'Le nouveau mot de passe ne respecte pas les règles indiquées',
      });
      return;
    }

    const password = this.passwordForm.get('password')?.value;
    if (password === this.temporaryPassword) {
      this.notificationService.addNotification({
        type: 'alert',
        message: 'Le nouveau mot de passe doit être différent du mot de passe temporaire',
      });
      return;
    }

    const request: FirstPasswordRequestDto = {
      username: this.username,
      current_password: this.temporaryPassword,
      password,
      password2: this.passwordForm.get('password2')?.value,
    };

    this.isSubmitting = true;

    this.auth.firstPassword(request).pipe(
      tap((token: TokenDto) => this.storageService.saveToken(token)),
      switchMap(() =>
        forkJoin({
          user: this.userService.me(),
          settings: this.settingsService.loadSettings()
        })
      )
    ).subscribe({
      next: ({ user }) => {
        this.storageService.saveInfoUser(user);
        this.notificationService.addNotification({
          type: 'success',
          message: 'Mot de passe enregistré, connexion SynthIA réussie',
        });
        this.router.navigateByUrl(user.agreement === 0 ? '/user/agreement' : '/chat');
      },
      error: (err: { status: number }) => {
        this.isSubmitting = false;

        if (err.status === 404) {
          this.notificationService.addNotification({
            type: 'alert',
            message: 'Mot de passe temporaire incorrect, merci de vous reconnecter',
          });
          this.router.navigateByUrl('/login');
        } else if (err.status === 423) {
          this.notificationService.addNotification({
            type: 'alert',
            message: 'Votre compte a été bloqué, merci de contacter un administrateur',
          });
          this.router.navigateByUrl('/login');
        } else if (err.status === 400) {
          this.notificationService.addNotification({
            type: 'alert',
            message: 'Le nouveau mot de passe a été refusé',
          });
        } else {
          this.notificationService.addNotification({
            type: 'alert',
            message: 'Une erreur est survenue lors du changement de mot de passe',
          });
        }
      }
    });
  }
}
